import { create } from 'zustand'
import axios from 'axios'
import { BACKEND } from '../utils/config'

const DEFAULTS = {
  photo_mrc_quality:       0.82,   // ← modifié (était 0.55) → ~2 Mo depuis photo brute
  photo_supabase_quality:  0.65,   // ← modifié (était 0.95) → ~1 Mo depuis photo brute
  photo_supabase_max_dim:  2048,
  upload_timeout_ms:       120_000,
  upload_max_retries:      5,
  upload_poll_interval_ms: 5_000,
  pdf_in_email:            true,
  email_enabled:           true,
  theme_mode:              'auto',  // 'auto', 'light', 'dark'
  theme_glass:             false,   // true = glassmorphism enabled
  theme_color:             'indigo', // 'indigo', 'blue', 'green', 'emerald', 'gray'
}

export type AppSettings = typeof DEFAULTS

function parse(raw: Record<string, string>): AppSettings {
  return {
    photo_mrc_quality:       parseFloat(raw.photo_mrc_quality       ?? String(DEFAULTS.photo_mrc_quality)),
    photo_supabase_quality:  parseFloat(raw.photo_supabase_quality  ?? String(DEFAULTS.photo_supabase_quality)),
    photo_supabase_max_dim:  parseInt  (raw.photo_supabase_max_dim  ?? String(DEFAULTS.photo_supabase_max_dim), 10),
    upload_timeout_ms:       parseInt  (raw.upload_timeout_ms       ?? String(DEFAULTS.upload_timeout_ms),      10),
    upload_max_retries:      parseInt  (raw.upload_max_retries      ?? String(DEFAULTS.upload_max_retries),      10),
    upload_poll_interval_ms: parseInt  (raw.upload_poll_interval_ms ?? String(DEFAULTS.upload_poll_interval_ms), 10),
    pdf_in_email:            String    (raw.pdf_in_email             ?? 'true') === 'true',
    email_enabled:           String    (raw.email_enabled            ?? 'true') === 'true',
    theme_mode:              String    (raw.theme_mode               ?? DEFAULTS.theme_mode),
    theme_glass:             String    (raw.theme_glass              ?? String(DEFAULTS.theme_glass)) === 'true',
    theme_color:             String    (raw.theme_color              ?? DEFAULTS.theme_color),
  }
}

interface SettingsStore {
  settings: AppSettings
  loaded:   boolean
  loading:  boolean
  error:    string | null

  fetchSettings:  (token: string) => Promise<void>
  updateSettings: (token: string, updates: Record<string, string>) => Promise<void>
  resetSettings:  (token: string) => Promise<void>
  getSetting:     <K extends keyof AppSettings>(key: K) => AppSettings[K]
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  settings: { ...DEFAULTS },
  loaded:   false,
  loading:  false,
  error:    null,

  fetchSettings: async (token) => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const { data } = await axios.get(`${BACKEND}/api/settings`, {
        headers: { 'x-mrc-auth': token },
      })
      if (data?.success && data?.settings) {
        set({ settings: parse(data.settings), loaded: true, loading: false })
        console.log('[settings] Chargés ✅')
      } else {
        throw new Error('Réponse inattendue')
      }
    } catch (e: any) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        console.info('[settings] Non-admin → DEFAULTS utilisés')
      } else {
        console.warn('[settings] Erreur réseau → DEFAULTS:', e.message)
      }
      set({ settings: { ...DEFAULTS }, loaded: true, loading: false, error: e.message })
    }
  },

  updateSettings: async (token, updates) => {
    try {
      const { data } = await axios.post(`${BACKEND}/api/settings`, { updates }, {
        headers: { 'x-mrc-auth': token },
      })
      if (data?.success && data?.settings) {
        set({ settings: parse(data.settings) })
        console.log('[settings] Mis à jour ✅', Object.keys(updates).join(', '))
      }
    } catch (e: any) {
      console.error('[settings] Erreur update:', e.message)
      throw e
    }
  },

  resetSettings: async (token) => {
    try {
      const { data } = await axios.post(`${BACKEND}/api/settings/reset`, {}, {
        headers: { 'x-mrc-auth': token },
      })
      if (data?.success && data?.settings) {
        set({ settings: parse(data.settings) })
        console.log('[settings] Reset ✅')
      }
    } catch (e: any) {
      console.error('[settings] Erreur reset:', e.message)
      set({ settings: { ...DEFAULTS } })
    }
  },

  getSetting: (key) => get().settings[key] ?? DEFAULTS[key],
}))